import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Header, Footer } from "../components/Layout";
import { VEHICLES, IMAGES } from "../data/vehicles";
import { GoFundMeWidget } from "../components/GoFundMeWidget";
import {
  fadeInUp,
  stagger,
  HERO_TITLE_EARLY,
  HERO_SUB,
  HERO_CTA,
  CHEVRON_BOUNCE,
  REVEAL_FROM_RIGHT,
  REVEAL_FADE,
  REVEAL_UP_DELAYED,
} from "../lib/animations";

const LINEUP_CARD_INITIAL = { opacity: 0, y: 30 };
const LINEUP_CARD_TARGET = { opacity: 1, y: 0 };
const lineupCardTransition = (index) => ({ duration: 0.6, delay: 0.2 + index * 0.1 });

// Hero Section
const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-screen overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y }}>
        <img 
          src={VEHICLES.adler.images[0]}
          alt="Adler Hypercar"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
      </motion.div>

      <motion.div 
        className="relative z-10 h-full flex flex-col justify-end pb-20 md:pb-32 px-6 md:px-8"
        style={{ opacity }}
      >
        <div className="max-w-[1400px] mx-auto w-full">
          <motion.h1 
            className="font-display text-[15vw] md:text-[12vw] lg:text-[10vw] leading-[0.85] tracking-tighter mb-6"
            {...HERO_TITLE_EARLY}
            data-testid="hero-title"
          >
            THE ANALOG<br />
            <span className="text-[#FF2A00]">REVOLUTION</span>
          </motion.h1>
          <motion.p 
            className="text-lg md:text-xl text-[#A3A3A3] mb-8 max-w-xl"
            {...HERO_SUB}
          >
            No computers. No electronics. Just machine.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4"
            {...HERO_CTA}
          >
            <Link to="/vehicles">
              <button 
                className="font-mono text-sm uppercase tracking-wider bg-[#FF2A00] text-white px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200"
                data-testid="hero-explore-btn"
              >
                Explore Vehicles
              </button>
            </Link>
            <Link to="/philosophy">
              <button 
                className="font-mono text-sm uppercase tracking-wider border border-white/30 text-white px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200"
                data-testid="hero-philosophy-btn"
              >
                Our Philosophy
              </button>
            </Link>
          </motion.div>
        </div>

        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          {...CHEVRON_BOUNCE}
        >
          <ChevronDown className="w-6 h-6 text-[#A3A3A3]" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Marquee Section
const MarqueeSection = () => (
  <div className="py-6 bg-[#FF2A00] overflow-hidden">
    <Marquee speed={60} gradient={false}>
      <span className="font-mono text-sm uppercase tracking-wider text-white mx-8">BUILT IN GERMANY</span>
      <span className="text-white mx-4">•</span>
      <span className="font-mono text-sm uppercase tracking-wider text-white mx-8">NO SCREENS</span>
      <span className="text-white mx-4">•</span>
      <span className="font-mono text-sm uppercase tracking-wider text-white mx-8">MECHANICAL BUTTONS</span>
      <span className="text-white mx-4">•</span>
      <span className="font-mono text-sm uppercase tracking-wider text-white mx-8">NO EVS</span>
      <span className="text-white mx-4">•</span>
      <span className="font-mono text-sm uppercase tracking-wider text-white mx-8">NO HYBRIDS</span>
      <span className="text-white mx-4">•</span>
      <span className="font-mono text-sm uppercase tracking-wider text-white mx-8">800 HORSEPOWER</span>
      <span className="text-white mx-4">•</span>
      <span className="font-mono text-sm uppercase tracking-wider text-white mx-8">MANUAL ONLY</span>
      <span className="text-white mx-4">•</span>
    </Marquee>
  </div>
);

// Philosophy Preview
const PhilosophyPreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-40 px-6 md:px-8" data-testid="philosophy-preview">
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-12 md:gap-24">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.p 
            variants={fadeInUp}
            className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF2A00] mb-6"
          >
            The Philosophy
          </motion.p>
          <motion.h2 
            variants={fadeInUp}
            className="font-display text-4xl md:text-6xl tracking-tight mb-8"
          >
            GUARANTEED FOR 10 YEARS.<br />BUILT FOR 100.
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-[#A3A3A3] leading-relaxed mb-6"
          >
            We believe in transportation and inspiration — nothing more, nothing less. 
            Every Keller Motors vehicle is built with cable-operated throttle, gated shifter, 
            mechanical suspension, and analog gauges. No drive-by-wire. No paddle shifters. 
            No compromises.
          </motion.p>
          <motion.p 
            variants={fadeInUp}
            className="text-[#A3A3A3] leading-relaxed mb-8"
          >
            When you buy a Keller, you participate in installing your own engine alongside 
            our certified technicians. We want our customers to understand their machine, 
            so they can keep it on the road for decades. This is not just a car — it's 
            integrity that you can drive.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/philosophy" className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider text-[#FF2A00] hover:text-white transition-colors">
              Read the Manifesto <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={REVEAL_FROM_RIGHT.initial}
          animate={isInView ? REVEAL_FROM_RIGHT.target : {}}
          transition={REVEAL_FROM_RIGHT.transition}
          className="relative"
        >
          <img 
            src={IMAGES.engine.dressed}
            alt="VQ35DET Engine"
            className="w-full h-auto border border-[#262626]"
          />
          <div className="absolute -bottom-6 -left-6 bg-[#0F0F0F] border border-[#262626] p-6">
            <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mb-1">Heart</p>
            <p className="font-display text-2xl">VQ35DET</p>
            <p className="text-sm text-[#A3A3A3]">800 HP • Co-developed with JWT</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Featured Vehicle
const FeaturedVehicle = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const adler = VEHICLES.adler;

  return (
    <section ref={ref} className="py-24 md:py-40 px-6 md:px-8 bg-[#0F0F0F]" data-testid="featured-vehicle">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="mb-12"
        >
          <motion.p variants={fadeInUp} className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF2A00] mb-4">
            The Flagship
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-display text-5xl md:text-7xl tracking-tight">
            ADLER
          </motion.h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div
            initial={REVEAL_FADE.initial}
            animate={isInView ? REVEAL_FADE.target : {}}
            transition={REVEAL_FADE.transition}
          >
            <img 
              src={adler.images[1]}
              alt="Adler"
              className="w-full h-auto border border-[#262626]"
            />
          </motion.div>

          <motion.div
            initial={REVEAL_UP_DELAYED.initial}
            animate={isInView ? REVEAL_UP_DELAYED.target : {}}
            transition={REVEAL_UP_DELAYED.transition}
            className="flex flex-col justify-center"
          >
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="border border-[#262626] p-6">
                <p className="font-display text-4xl md:text-5xl text-[#FF2A00]">800</p>
                <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3]">Horsepower</p>
              </div>
              <div className="border border-[#262626] p-6">
                <p className="font-display text-4xl md:text-5xl">2,000</p>
                <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3]">Pounds</p>
              </div>
              <div className="border border-[#262626] p-6">
                <p className="font-display text-4xl md:text-5xl">2.5</p>
                <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3]">0-60 Seconds</p>
              </div>
              <div className="border border-[#262626] p-6">
                <p className="font-display text-4xl md:text-5xl">3,000</p>
                <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3]">Downforce (lbs)</p>
              </div>
            </div>

            <p className="text-[#A3A3A3] leading-relaxed mb-8">{adler.description}</p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/vehicles/adler">
                <button className="font-mono text-sm uppercase tracking-wider bg-[#FF2A00] text-white px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200 w-full sm:w-auto">
                  Reserve — {adler.price}
                </button>
              </Link>
              <Link to="/vehicles/adler">
                <button className="font-mono text-sm uppercase tracking-wider border border-[#262626] text-white px-8 py-4 hover:bg-[#262626] transition-colors duration-200 w-full sm:w-auto">
                  Full Specs
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Lineup Preview
const LineupPreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const featuredVehicles = [
    VEHICLES.adler,
    VEHICLES.falke,
    VEHICLES.quelle,
    VEHICLES.ritter,
    VEHICLES.koenig,
    VEHICLES.stein,
    VEHICLES.enz,
    VEHICLES.panzer,
  ];

  return (
    <section ref={ref} className="py-24 md:py-40 px-6 md:px-8" data-testid="lineup-preview">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12"
        >
          <div>
            <motion.p variants={fadeInUp} className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF2A00] mb-4">
              The Lineup
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-6xl tracking-tight">
              EIGHT MACHINES.<br />ONE PHILOSOPHY.
            </motion.h2>
          </div>
          <motion.div variants={fadeInUp}>
            <Link to="/vehicles" className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider text-[#FF2A00] hover:text-white transition-colors mt-6 md:mt-0">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {featuredVehicles.map((vehicle, index) => (
            <motion.div
              key={vehicle.id}
              initial={LINEUP_CARD_INITIAL}
              animate={isInView ? LINEUP_CARD_TARGET : {}}
              transition={lineupCardTransition(index)}
              className="group"
            >
              <Link to={`/vehicles/${vehicle.id}`}>
                <div className="border border-[#262626] hover:border-[#FF2A00] transition-colors duration-300 overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden bg-[#0F0F0F]">
                    {vehicle.images[0] ? (
                      <img 
                        src={vehicle.images[0]}
                        alt={vehicle.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3]">Design Pending</p>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <p className="font-mono text-xs uppercase tracking-wider text-[#FF2A00] mb-2">{vehicle.edition}</p>
                    <h3 className="font-display text-2xl mb-2">{vehicle.name}</h3>
                    <p className="text-sm text-[#A3A3A3] mb-4">{vehicle.tagline}</p>
                    <div className="flex justify-between items-center">
                      <span className="font-mono text-sm text-white">{vehicle.price}</span>
                      <ArrowRight className="w-4 h-4 text-[#A3A3A3] group-hover:text-[#FF2A00] transition-colors" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="relative py-32 md:py-48 px-6 md:px-8 overflow-hidden" data-testid="cta-section">
      <div className="absolute inset-0">
        <img 
          src={VEHICLES.adler.images[2]}
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-[#050505]" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto text-center">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight mb-6"
          >
            JOIN THE<br /><span className="text-[#FF2A00]">REVOLUTION</span>
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-lg md:text-xl text-[#A3A3A3] mb-8 max-w-2xl mx-auto"
          >
            Reserve your place in this piece of automotive history. 
            Be part of the movement that's bringing passion back to the automobile.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/vehicles">
              <button className="font-mono text-sm uppercase tracking-wider bg-[#FF2A00] text-white px-10 py-5 hover:bg-white hover:text-black transition-colors duration-200">
                Reserve a Vehicle
              </button>
            </Link>
            <Link to="/contact">
              <button className="font-mono text-sm uppercase tracking-wider border border-white/30 text-white px-10 py-5 hover:bg-white hover:text-black transition-colors duration-200">
                Contact Us
              </button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Support Section — GoFundMe campaign to launch the Analog Revolution
const SupportSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-32 px-6 md:px-8 bg-[#0F0F0F] border-y border-[#262626]" data-testid="support-section">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.p variants={fadeInUp} className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF2A00] mb-6">
            Fund the Revolution
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-6xl tracking-tight mb-6">
            HUMILIATE THE LUCE.<br />LAUNCH THE ANALOG REVOLUTION.
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-[#A3A3A3] leading-relaxed mb-4">
            Every contribution puts us closer to a running Adler prototype, a proper workshop, and the first
            production run of vehicles built on mechanical honesty instead of software subscriptions.
          </motion.p>
          <motion.p variants={fadeInUp} className="text-white leading-relaxed">
            Back the campaign. Own a piece of the story.
          </motion.p>
        </motion.div>

        <motion.div
          initial={REVEAL_FADE.initial}
          animate={isInView ? REVEAL_FADE.target : {}}
          transition={REVEAL_FADE.transition}
          className="border border-[#262626] bg-[#050505] p-6 md:p-8"
          data-testid="support-widget-wrapper"
        >
          <GoFundMeWidget />
        </motion.div>
      </div>
    </section>
  );
};

export default function HomePage() {
  return (
    <main className="min-h-screen bg-[#050505]">
      <Header />
      <HeroSection />
      <MarqueeSection />
      <PhilosophyPreview />
      <div className="section-divider max-w-[1400px] mx-auto" />
      <FeaturedVehicle />
      <LineupPreview />
      <SupportSection />
      <CTASection />
      <Footer />
    </main>
  );
}
